import { useQuery } from "@tanstack/react-query";
import { axios } from "./axios";
import { useAuth } from "../context/AuthContext";
import { MandateType } from "../types/MandateType";
import { env } from "../utils/env";
import { useNavigate } from "react-router-dom";
import { ROUTE } from "../routes";

type MandateTypeFilter = "entity" | "individual";

/**
 * Division filter for the list, sent as `?divisions=1,2,3` (Application
 * Types aren't scoped to Teams). Omitted from the request when empty.
 */
export type MandateTypeScope = {
  divisions?: number[];
};

const scopeParams = (scope?: MandateTypeScope) => ({
  ...(scope?.divisions?.length
    ? { divisions: [...scope.divisions].sort((a, b) => a - b).join(",") }
    : {}),
});

const fetchMandateTypes =
  (authHeader: string, type?: MandateTypeFilter, scope?: MandateTypeScope) =>
  () =>
    axios.get<MandateType[]>(
      `${env.verifyServiceUrl}individual/mandate_type/`,
      {
        headers: {
          Authorization: authHeader,
        },
        params: {
          ...(type ? { _type: type } : {}),
          ...scopeParams(scope),
        },
      }
    );

export const useMandateTypes = (
  type?: MandateTypeFilter,
  scope?: MandateTypeScope,
) => {
  const { authHeader } = useAuth();
  const navigate = useNavigate();

  return useQuery(
    ["mandate_types", type, scopeParams(scope)],
    fetchMandateTypes(authHeader, type, scope),
    {
      retry: false,
      keepPreviousData: true,
      onError: (err: any) => {
        const status = err?.response?.status ?? err?.request?.status;
        if (status === 500 || status === 503) {
          navigate(ROUTE.SERVER_ERROR);
        }
      },
    }
  );
};
